import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type OpenPortalResult } from '@whinerp/client';
import { Button, PortalField, PortalShell, Skeleton } from '@whinerp/ui';
import { formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { portalFieldLabel, portalShellLabels } from './portal-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-36 Booking page.
 *
 * The third caller of PortalShell, and the one with the least to show: a
 * booking page needs a name and a list of times, and nothing else about the
 * person it belongs to. Its allowlist reflects that - `full_name` and `email`,
 * where the customer portal also has a job title.
 *
 * Availability is generated from the workspace's rules minus what is already
 * taken, in the WORKSPACE's timezone. A prospect in London booking a Dubai
 * team's calendar sees the team's working day converted for them, rather than
 * nine-to-five in their own - which would be inside the office's day only on
 * the occasions it happened to line up.
 *
 * A slot taken while somebody was choosing re-renders availability and keeps
 * what they typed. Losing a typed note to a race is how a booking page becomes
 * an email instead.
 */

type Portal = OpenPortalResult;

function BookingScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const params = useParams();
  // The sheet's route is /book/:slug. The slug is the link, and it is read as
  // a portal token: a booking page addressed by a guessable workspace slug
  // would be a booking page anybody can enumerate.
  const token = params['slug'] ?? '';
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [taken, setTaken] = useState(false);
  const [booked, setBooked] = useState<string | null>(null);

  const page = useQuery<Portal, ApiError>({
    queryKey: ['portal', 'booking', token],
    queryFn: () => api.openPortal({ path: { surface: 'booking' }, query: { token } }),
  });

  const book = useMutation({
    mutationFn: (slot: { userId: string; startsAt: string; endsAt: string }) =>
      api.bookSlot({ body: { token, ...slot, note: note.trim() || null } }),
    onSuccess: (result) => {
      /*
       * A slot taken while somebody was choosing.
       *
       * Availability re-renders and the note stays in the box - 04_Screens
       * asks for exactly this, and the reason is that losing a typed note to a
       * race is how a booking page becomes an email instead.
       */
      setTaken(result.taken);
      setBooked(result.bookingId);
      void queryClient.invalidateQueries({ queryKey: ['portal', 'booking', token] });
    },
  });

  if (page.isPending) return <BookingLoading />;

  /*
   * A failed request is a refusal page, not an empty content pane.
   *
   * Without this the shell rendered its content region with no data in it, and
   * a test asserting the region exists passed while the API was answering 500.
   * A customer would have seen a branded page with nothing on it and no way to
   * tell whether that meant "you have nothing" or "this is broken".
   */
  if (page.isError || !page.data) return <BookingError />;

  const data = page.data;
  const slots = data?.slots ?? [];

  return (
    <PortalShell
      surface="booking"
      workspaceName={data?.branding?.workspaceName ?? null}
      refusal={data?.refusal ?? null}
      labels={portalShellLabels(t, data?.branding?.workspaceName ?? null)}
    >
      <h2 className="text-lead text-ink">{t('booking.title')}</h2>
      <p className="text-body text-ink-meta">{t('booking.intro')}</p>

      {(data?.contact ?? []).map((field) => (
        <PortalField
          key={field.key}
          data-portal-field={field.key}
          label={portalFieldLabel(t, field.key)}
          value={field.value}
        />
      ))}

      <label className="flex flex-col gap-1 text-dense text-ink">
        {t('booking.note')}
        <textarea
          data-booking-note
          aria-label={t('booking.note')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
        />
      </label>

      {taken ? (
        <p role="alert" data-slot-taken className="rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text">
          {t('booking.taken')}
        </p>
      ) : null}

      {booked ? (
        <p role="status" data-booking-confirmed className="rounded-card bg-success-tint px-3 py-2 text-dense text-success-text">
          {t('booking.confirmed')}
        </p>
      ) : slots.length === 0 ? (
        /* No times, said plainly. An empty calendar grid reads as a
           fully-booked week, which is a different and worse message than
           "nobody has set this up". */
        <div data-no-availability className="flex flex-col gap-2 rounded-card bg-sunken px-3 py-2">
          <span className="text-body text-ink">{t('booking.noSlots')}</span>
          <span>
            <Button intent="secondary" data-request-time onClick={() => setTaken(false)}>
              {t('portal.requestNewLink')}
            </Button>
          </span>
        </div>
      ) : (
        <ul data-slots className="flex flex-wrap gap-2">
          {slots.map((slot) => (
            <li key={`${slot.startsAt}-${slot.userId}`}>
              <Button
                intent="secondary"
                data-slot={slot.startsAt}
                data-slot-user={slot.userId}
                busy={book.isPending}
                onClick={() =>
                  book.mutate({ userId: slot.userId, startsAt: slot.startsAt, endsAt: slot.endsAt })
                }
              >
                {/* Through the one formatter, in the reader's own locale but
                    the workspace's zone. */}
                {formatDateTime(slot.startsAt, format)}
              </Button>
            </li>
          ))}
        </ul>
      )}

    </PortalShell>
  );
}

function BookingEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('booking.noSlots')}</p>;
}

function BookingLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen w-full flex-col items-center bg-canvas px-gutter py-6" role="status" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <div className="flex w-full max-w-reading flex-col gap-3">
        <Skeleton className="h-line-h2 w-panel-wide" />
        {/* Calendar-shaped, so the page does not jump when it lands. */}
        <Skeleton className="h-plot w-full" />
      </div>
    </div>
  );
}

function BookingError(): JSX.Element {
  const t = useT();
  return (
    <PortalShell
      surface="booking"
      workspaceName={null}
      refusal={{ kind: 'invalid', canRequestNewLink: false }}
      labels={portalShellLabels(t, null)}
    >
      <span />
    </PortalShell>
  );
}

export const screen = defineScreen({
  id: 'SC-36',
  route: '/book/:slug',
  surface: 'page',
  chrome: 'bare',
  title: 'Book a time',
  features: ['F-057'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: ['PortalShell'],
  elements: ['PortalShell', 'PortalField'],
  // A real link, so every check audits the page a customer actually
  // opens rather than its refusal state.
  samplePortalToken: { surface: 'booking', param: 'slug' },
  states: { empty: BookingEmpty, loading: BookingLoading, error: BookingError },
  component: BookingScreen,
});

export default screen;
